import { memo, type ReactNode } from "react";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { CrmMessageFranchiseUsagePanel } from "../../statistics";
import CrmSetupConfigurationOptionsPanel from "../../setup/components/CrmSetupConfigurationOptionsPanel";
import CrmSetupStepContentCard from "../../setup/components/steps/CrmSetupStepContentCard";
import CrmSetupContractMetaBillingSection from "../../setup/components/steps/CrmSetupContractMetaBillingSection";
import type { UseCrmSetupConfigurationResult } from "../../setup/hooks/useCrmSetupConfiguration";
import type { CrmSetupConfigurationOption } from "../../setup/types";
import { useWhatsAppConfigurationBillingActions } from "../hooks/useWhatsAppConfigurationBillingActions";
import DialogContentText from "@mui/material/DialogContentText";
import Chip from "@mui/material/Chip";

import { WHATSAPP_CHANNEL_AUTOMATIONS_IMPACT_COPY } from "../constants/whatsappChannelAutomationsCopy";

export interface WhatsAppConfigurationBillingActionsProps {
  organizationId?: string;
  channelOption: CrmSetupConfigurationOption | null | undefined;
  setupConfiguration: UseCrmSetupConfigurationResult;
  hasFluxZapExtensionActive?: boolean;
  isFluxZapExtensionLoading?: boolean;
  onFluxZapExtensionActivated?: () => void;
  metaContactsSlot?: ReactNode;
  franchiseStatsRefreshSignal?: number;
  onFluxmedCheckoutCompleted?: () => void;
  hasConfiguredAutomations?: boolean;
  removeAllAutomationConfigs?: () => Promise<void>;
  persistedWabaId?: string;
  onAfterChannelChange?: () => void | Promise<void>;
}

const WhatsAppConfigurationBillingActions = memo(
  ({
    organizationId,
    channelOption,
    setupConfiguration,
    hasFluxZapExtensionActive = false,
    isFluxZapExtensionLoading = false,
    metaContactsSlot,
    franchiseStatsRefreshSignal = 0,
    onFluxmedCheckoutCompleted,
    hasConfiguredAutomations = false,
    removeAllAutomationConfigs,
    persistedWabaId,
    onAfterChannelChange,
  }: WhatsAppConfigurationBillingActionsProps) => {
    const {
      handleFluxmedCheckoutCompleted,
      isMetaChannel,
      isFluxmedChannel,
      isLinkingWhatsAppAccount,
      canSaveMetaEndpoint,
      activePlanLabel,
      primaryPlanButtonLabel,
      planDialogOpen,
      handlePrimaryPlanButtonClick,
      closePlanDialog,
      handlePlanDialogClose,
      handleSaveMetaEndpoint,
      metaSaveConfirmOpen,
      handleConfirmMetaSave,
      handleCancelMetaSave,
    } = useWhatsAppConfigurationBillingActions({
      channelOption,
      setupConfiguration,
      hasFluxZapExtensionActive,
      isFluxZapExtensionLoading,
      onFluxmedCheckoutCompleted,
      hasConfiguredAutomations,
      removeAllAutomationConfigs,
      persistedWabaId,
      onAfterChannelChange,
    });
    const planDialogTitle = isLinkingWhatsAppAccount
      ? "Vincular conta WhatsApp"
      : "Canal de envio (Meta ou Fluxmed)";
    // A extensão FluxZap só bloqueia a vinculação com a Meta. O aviso/assinatura
    // aparece como contexto da opção Meta, sem ocultar o restante do painel
    // (número padrão Fluxmed continua disponível sem a extensão).
    const showFluxZapExtensionNotice =
      !isFluxZapExtensionLoading && !hasFluxZapExtensionActive;
    const channelGuidance = isMetaChannel
      ? {
        title: "Canal Meta conectado",
        description:
          "Escolha abaixo o número preferencial do WABA. Limites e cobrança seguem sua assinatura do WhatsApp Business.",
      }
      : channelOption == null
        ? {
          title: "Escolha um canal para continuar",
          description:
            "Após selecionar o canal, os atalhos de cobrança e a lista de números (quando Meta) serão exibidos nesta área.",
        }
        : {
          title: "Canal Fluxmed ativo",
          description:
            "Contrate pacotes na área abaixo e acompanhe a franquia no mesmo fluxo do passo de contrato do setup.",
        };

    return (
      <>
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Typography variant="subtitle2" fontWeight={700}>
              Plano e mensagens
            </Typography>
            <Chip
              size="small"
              label={activePlanLabel}
              color="primary"
              variant={channelOption == null ? "outlined" : "filled"}
              sx={{ fontWeight: 700 }}
            />
          </Stack>

          <Box
            sx={{
              p: 1,
              borderRadius: 1.5,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.default",
            }}
          >
            <Typography variant="caption" color="text.primary" fontWeight={500}>
              {channelGuidance.title}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
              {channelGuidance.description}
            </Typography>
          </Box>
          {channelOption != null && hasConfiguredAutomations ? (
            <Alert severity="info" sx={{ py: 0.5 }}>
              {WHATSAPP_CHANNEL_AUTOMATIONS_IMPACT_COPY.alert}
            </Alert>
          ) : null}
          {isMetaChannel && metaContactsSlot != null ? (
            <Box sx={{ mt: 0.25, width: "100%" }}>{metaContactsSlot}</Box>
          ) : null}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            flexWrap="wrap"
            useFlexGap
          >
            <Button
              variant="contained"
              color="primary"
              type="button"
              onClick={handlePrimaryPlanButtonClick}
              disabled={isLinkingWhatsAppAccount && isFluxZapExtensionLoading}
              sx={{ textTransform: "none" }}
            >
              {primaryPlanButtonLabel}
            </Button>
          </Stack>
          {channelOption == null ? (
            <Typography variant="caption" color="text.secondary">
              Use «Ativar plano de mensagens» para escolher Fluxmed ou Meta. Com
              plano ativo, o botão mostra «Vincular conta WhatsApp».
            </Typography>
          ) : null}
          {isFluxmedChannel ? (
            <>
              <Box sx={{ mt: 1 }}>
                <CrmSetupStepContentCard
                  title="Contrato e pacotes (Fluxmed)"
                  description="Compra de pacotes de mensagens para o CRM Clínico, no modelo do FluxZap."
                  billingVariant="fluxmed"
                  onFluxmedContractActivated={handleFluxmedCheckoutCompleted}
                />
              </Box>
              <CrmMessageFranchiseUsagePanel
                organizationId={organizationId}
                statsRefreshSignal={franchiseStatsRefreshSignal}
                sx={{ mt: 0.5 }}
              />
            </>
          ) : null}
        </Stack>

        {planDialogOpen && (
          <Dialog
            open={planDialogOpen}
            onClose={handlePlanDialogClose}
            fullWidth
            maxWidth="lg"
            scroll="paper"
            aria-labelledby="whatsapp-plan-dialog-title"
          >
            <DialogTitle id="whatsapp-plan-dialog-title">
              {planDialogTitle}
            </DialogTitle>
            <DialogContent dividers sx={{ pt: 2 }}>
              {showFluxZapExtensionNotice ? (
                <Stack spacing={3} sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      borderRadius: 3,
                      p: { xs: 2.5, sm: 3 },
                      color: "common.white",
                      background: (theme) =>
                        `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      boxShadow: 3,
                    }}
                  >
                    <Box
                      aria-hidden
                      sx={{
                        position: "absolute",
                        top: -40,
                        right: -30,
                        width: 160,
                        height: 160,
                        borderRadius: "50%",
                        bgcolor: "common.white",
                        opacity: 0.08,
                      }}
                    />
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="flex-start"
                      sx={{ position: "relative" }}
                    >
                      <Box
                        sx={{
                          flexShrink: 0,
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "rgba(255,255,255,0.16)",
                        }}
                      >
                        <LockOutlinedIcon sx={{ fontSize: 26 }} />
                      </Box>
                      <Stack spacing={0.75}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                          useFlexGap
                        >
                          <Typography variant="subtitle1" fontWeight={800}>
                            Extensão FluxZap não está ativa
                          </Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ opacity: 0.92 }}>
                          {isLinkingWhatsAppAccount
                            ? "Para vincular sua conta WhatsApp via Meta, contrate a extensão FluxZap abaixo para liberar a vinculação."
                            : "A configuração Meta (número próprio via WhatsApp Business) exige a extensão FluxZap ativa. Contrate a extensão FluxZap abaixo para continuar."}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>

                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 3,
                      p: { xs: 2, sm: 2.5 },
                      bgcolor: "background.paper",
                    }}
                  >
                    <Stack spacing={0.25} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight={800}>
                        Ativar extensão FluxZap
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Assine para desbloquear a vinculação com a Meta diretamente por aqui.
                      </Typography>
                    </Stack>
                    <CrmSetupContractMetaBillingSection />
                  </Box>
                </Stack>
              ) : null}
              {!showFluxZapExtensionNotice ? (
                <CrmSetupConfigurationOptionsPanel
                  {...setupConfiguration}
                  isFluxZapExtensionActive={hasFluxZapExtensionActive}
                  hasConfiguredAutomations={hasConfiguredAutomations}
                  removeAllAutomationConfigs={removeAllAutomationConfigs}
                  persistedWhatsappChannelOption={
                    channelOption as CrmSetupConfigurationOption | null
                  }
                  onAfterChannelChange={onAfterChannelChange}
                />
              ) : null}
            </DialogContent>
            <DialogActions>
              {!showFluxZapExtensionNotice ? (
                <Button
                  variant="contained"
                  type="button"
                  onClick={() => void handleSaveMetaEndpoint()}
                  disabled={
                    !hasFluxZapExtensionActive ||
                    !canSaveMetaEndpoint ||
                    setupConfiguration.isSavingMetaAccountSelection
                  }
                  sx={{ textTransform: "none" }}
                >
                  {setupConfiguration.isSavingMetaAccountSelection
                    ? "Salvando..."
                    : "Salvar canal Meta"}
                </Button>
              ) : null}
              <Button
                type="button"
                onClick={closePlanDialog}
                sx={{ textTransform: "none" }}
              >
                Fechar
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {metaSaveConfirmOpen ? (
        <Dialog
          open={metaSaveConfirmOpen}
          onClose={handleCancelMetaSave}
          fullWidth
          maxWidth="sm"
          aria-labelledby="whatsapp-meta-save-automations-dialog-title"
        >
          <DialogTitle id="whatsapp-meta-save-automations-dialog-title">
            Alterar canal de envio?
          </DialogTitle>
          <DialogContent dividers>
            <DialogContentText color="text.primary">
              {WHATSAPP_CHANNEL_AUTOMATIONS_IMPACT_COPY.dialogParagraph}
            </DialogContentText>
            <DialogContentText color="warning.main" sx={{ mt: 1.5, fontWeight: 600 }}>
              {WHATSAPP_CHANNEL_AUTOMATIONS_IMPACT_COPY.dialogHighlight}
            </DialogContentText>
            <DialogContentText color="text.secondary" sx={{ mt: 1 }}>
              {WHATSAPP_CHANNEL_AUTOMATIONS_IMPACT_COPY.dialogSystemDefaultsNote}
            </DialogContentText>
            <DialogContentText sx={{ mt: 2 }}>
              Deseja continuar?
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button
              type="button"
              onClick={handleCancelMetaSave}
              color="inherit"
              sx={{ textTransform: "none" }}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={() => void handleConfirmMetaSave()}
              color="primary"
              variant="contained"
              sx={{ textTransform: "none" }}
            >
              Alterar canal
            </Button>
          </DialogActions>
        </Dialog>
        ) : null}
      </>
    );
  },
);

WhatsAppConfigurationBillingActions.displayName =
  "WhatsAppConfigurationBillingActions";

export default WhatsAppConfigurationBillingActions;
